<script setup>
import { ref } from 'vue'
import { ST } from '@/constants/ST'

const props = defineProps({
  categorias: { type: Array, required: true },
  nivel: { type: Number, default: 1 },
  isReadOnly: { type: Boolean, default: false },
  niveisMaximos: { type: Number, default: 4 },
  lstTipoMovimentacaoHeranca: { type: String, default: '' },
  listas: { type: Object, default: () => ({}) },
  ocultarExclusao: { type: Boolean, default: false },
  expandidos: { type: Set, default: () => new Set() }
})

const emit = defineEmits([
  'adicionar-filho',
  'editar',
  'excluir',
  'mover',
  'update:expandidos'
])

// --- Expand/Colapse (centralizado no pai) ---
const isExpanded = (id) => props.expandidos.has(id)

const toggleExpand = (id) => {
  const novoSet = new Set(props.expandidos)
  if (novoSet.has(id)) {
    novoSet.delete(id)
  } else {
    novoSet.add(id)
  }
  emit('update:expandidos', novoSet)
}

// --- Drag-and-Drop ---
const draggingId = ref(null)
const dragOverId = ref(null)

const onDragStart = (event, cat) => {
  draggingId.value = cat.idCategoriaFinanceira
  event.dataTransfer.effectAllowed = 'move'
  event.dataTransfer.setData('text/plain', String(cat.idCategoriaFinanceira))
}

const onDragOver = (event, cat) => {
  event.preventDefault()
  dragOverId.value = cat.idCategoriaFinanceira
}

const onDragLeave = (event) => {
  const relatedTarget = event.relatedTarget
  const currentNode = event.currentTarget
  if (currentNode && relatedTarget && currentNode.contains(relatedTarget)) {
    return
  }
  dragOverId.value = null
}

const onDrop = (event, catDestino) => {
  event.preventDefault()
  dragOverId.value = null
  const itemId = Number(event.dataTransfer.getData('text/plain'))
  if (!itemId || itemId === catDestino.idCategoriaFinanceira) {
    draggingId.value = null
    return
  }
  emit('mover', { itemId, destinoId: catDestino.idCategoriaFinanceira })
  draggingId.value = null
}

// --- Helpers ---
const getTextoLista = (lista, codigo) => {
  if (!lista || !codigo) return codigo || '-'
  const item = lista.find(i => String(i.idListaClienteItem) === String(codigo))
  return item ? item.textoLista : codigo
}

const getTipoBadgeClass = (tipo) => {
  if (!tipo) return 'bg-secondary'
  const t = tipo.toUpperCase()
  if (t.includes('RECEITA') || t.includes('ENTRADA')) return 'bg-success'
  if (t.includes('DESPESA') || t.includes('SAÍDA') || t.includes('SAIDA')) return 'bg-danger'
  if (t.includes('CUSTO')) return 'bg-warning text-dark'
  return 'bg-info text-dark'
}

const getStatusBadgeClass = (status) => {
  if (status === ST.Inativo) return 'bg-danger-subtle text-danger border border-danger-subtle'
  return 'bg-body-tertiary text-body border border-secondary-subtle'
}
</script>

<template>
  <div v-for="cat in categorias" :key="cat.idCategoriaFinanceira" class="tree-node-container">
    <!-- Node Row -->
    <div class="tree-node d-flex align-items-center gap-1 py-1 px-2 rounded"
         :class="{
           'bg-body-tertiary': dragOverId === cat.idCategoriaFinanceira,
           'border border-primary': draggingId === cat.idCategoriaFinanceira
         }"
         :draggable="!isReadOnly"
         @dragstart="(e) => onDragStart(e, cat)"
         @dragover="(e) => onDragOver(e, cat)"
         @dragleave="onDragLeave"
         @drop="(e) => onDrop(e, cat)">

      <!-- Indentação por nível (antes do botão de collapse) -->
      <span v-for="i in (nivel - 1)" :key="i" style="width: 28px; display: inline-block; flex-shrink: 0;"></span>

      <!-- Grip handle -->
      <span class="text-body-tertiary" style="cursor: grab; width: 16px; flex-shrink: 0;">
        <i class="bi bi-grip-vertical"></i>
      </span>

      <!-- Expand/Colapse toggle -->
      <button v-if="cat.subcategorias && cat.subcategorias.length > 0"
              type="button" class="btn btn-sm p-0 border-0"
              style="width: 20px; height: 20px; flex-shrink: 0;"
              @click.stop="toggleExpand(cat.idCategoriaFinanceira)">
        <i :class="['bi', isExpanded(cat.idCategoriaFinanceira) ? 'bi-chevron-down' : 'bi-chevron-right']"></i>
      </button>
      <span v-else style="width: 20px; display: inline-block; flex-shrink: 0;"></span>

      <!-- Botão + Adicionar filho (ou spacer para alinhamento) -->
      <button v-if="!isReadOnly && !cat.permiteLancamento && nivel < niveisMaximos"
              type="button" class="btn btn-sm btn-outline-secondary py-0"
              @click.stop="$emit('adicionar-filho', { paiId: cat.idCategoriaFinanceira, nivel: cat.nivel, lstTipoMovimentacao: cat.lstTipoMovimentacao })"
              title="Adicionar subcategoria">
        <i class="bi bi-plus-lg"></i>
      </button>
      <span v-else style="width: 28px; display: inline-block; flex-shrink: 0;"></span>

      <!-- Código Estruturado -->
      <span class="text-muted small font-monospace me-1" style="min-width: 10px; flex-shrink: 0;">
        {{ cat.codigoEstruturado }} -
      </span>

      <!-- Nome -->
      <span class="fw-medium flex-grow-1 text-truncate">{{ cat.nome }}</span>

      <!-- Permite Lançamento -->
      <span v-if="cat.permiteLancamento" class="badge bg-success me-1 flex-shrink-0" title="Permite lançamentos financeiros">
        <i class="bi bi-check-circle"></i> Lançamento
      </span>

      <!-- Tipo de Movimentação -->
      <span class="badge me-1 flex-shrink-0" :class="getTipoBadgeClass(cat.lstTipoMovimentacao || lstTipoMovimentacaoHeranca)">
        {{ getTextoLista(listas.LstTipoMovimentacao, cat.lstTipoMovimentacao) || cat.lstTipoMovimentacaoTexto || '---' }}
      </span>

      <!-- Status (Inativo = badge vermelho clarinho) -->
      <span class="badge me-1 flex-shrink-0" :class="getStatusBadgeClass(cat.lstStatus)">
        {{ getTextoLista(listas.LstStatus, cat.lstStatus) || '-' }}
      </span>

      <!-- Ações -->
      <div class="d-flex gap-1 flex-shrink-0" v-if="!isReadOnly">
        <button type="button" class="btn-icon text-primary btn-sm" @click.stop="$emit('editar', cat)" title="Editar">
          <i class="bi bi-pencil-fill"></i>
        </button>
        <button type="button" class="btn-icon text-danger btn-sm"
                @click.stop="$emit('excluir', cat)" title="Excluir" v-if="!cat.isSystem && !ocultarExclusao">
          <i class="bi bi-trash-fill"></i>
        </button>
      </div>
    </div>

    <!-- Subcategorias (recursivo) -->
    <div v-if="cat.subcategorias && cat.subcategorias.length > 0 && isExpanded(cat.idCategoriaFinanceira)">
      <TreeViewCategoria
        :categorias="cat.subcategorias"
        :nivel="nivel + 1"
        :isReadOnly="isReadOnly"
        :niveisMaximos="niveisMaximos"
        :lstTipoMovimentacaoHeranca="cat.lstTipoMovimentacao || lstTipoMovimentacaoHeranca"
        :listas="listas"
        :ocultarExclusao="ocultarExclusao"
        :expandidos="expandidos"
        @update:expandidos="(novoSet) => emit('update:expandidos', novoSet)"
        @adicionar-filho="$emit('adicionar-filho', $event)"
        @editar="$emit('editar', $event)"
        @excluir="$emit('excluir', $event)"
        @mover="$emit('mover', $event)"
      />
    </div>
  </div>
</template>

<style scoped>
.tree-node-container {
  border-bottom: 1px solid var(--bs-border-color);
}

.tree-node:hover {
  background-color: var(--bs-tertiary-bg);
}

.tree-node[draggable="true"] {
  cursor: grab;
}

.tree-node[draggable="true"]:active {
  cursor: grabbing;
}

.btn-icon {
  background: none;
  border: none;
  padding: 2px 6px;
  transition: opacity 0.2s;
}

.btn-icon:hover {
  opacity: 0.7;
}
</style>
